import { useState, useEffect, useCallback } from 'react';
import { ProcessingStatus } from '../types';
import TalRevAPI, { handleApiError } from '../services/api';

export const useProcessingStatus = (id: string | undefined) => {
  const [status, setStatus] = useState<ProcessingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!id) return;

    try {
      setError(null);
      const processingStatus = await TalRevAPI.getProcessingStatus(id);
      setStatus(processingStatus);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      console.error('Failed to fetch processing status:', err);
    } finally {
      setLoading(false);
    }
  }, [id]); // fetchStatus is stable due to useCallback with [id] dependency

  // Poll for status updates while processing
  useEffect(() => {
    if (!id) return;

    fetchStatus();

    let pollInterval: NodeJS.Timeout | null = null;

    // Set up polling that continues until completion
    const startPolling = () => {
      pollInterval = setInterval(async () => {
        try {
          const currentStatus = await TalRevAPI.getProcessingStatus(id);
          setStatus(currentStatus);

          // Stop polling when processing is complete, failed, or error
          if (currentStatus.status === 'completed' || currentStatus.status === 'error' || currentStatus.status === 'failed') {
            if (pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
            setLoading(false);
          }
        } catch (err) {
          const errorMessage = handleApiError(err);
          setError(errorMessage);
          console.error('Failed to fetch processing status during polling:', err);
          
          // Stop polling if session is not found (404 error)
          if (err && typeof err === 'object' && 'response' in err && 
              (err as any).response?.status === 404) {
            if (pollInterval) {
              clearInterval(pollInterval);
              pollInterval = null;
            }
            setLoading(false);
          }
        }
      }, 3000); // Poll every 3 seconds for responsive updates
    };

    // Start polling immediately
    startPolling();

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [id, fetchStatus]);

  const retry = useCallback(async () => {
    if (!id) return;

    try {
      setError(null);
      setLoading(true);
      
      // In a real implementation, we might have a retry endpoint
      // For now, we'll just fetch the status again
      await fetchStatus();
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      console.error('Failed to retry processing:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return {
    status,
    loading,
    error,
    retry,
    refetch: fetchStatus,
  };
};